import React, { useEffect, useState, useRef, useCallback, createContext, useContext } from 'react';
import { io } from 'socket.io-client';
import Swal from 'sweetalert2';
import { useNotification } from './NotificationContext';
import environmentConfig from '../config/config';
import ProgressOverlay from '../components/common/ProgressOverlay';
// [당첨 축하 팝업] 경품 성공 팝업 본문(3D·축하 문구·전송 정보)과 축하 효과
import { createRoot } from 'react-dom/client';
import RewardPrizeContent from '../components/common/RewardPrizeContent';
import { celebrate } from '../utils/celebrate';
// [당첨 축하 팝업 미리 받기] rewardStarted 때 상품 정보·3D·이미지 미리 받기
import { prefetchRewardPrize } from '../utils/rewardPrefetch';
// [글래스 로그인 팝업 2026-10-26] 글래스가 참가자를 조회했을 때 진행자 화면 팝업
import GlassLoginPopup from '../components/common/GlassLoginPopup';
// [경품 성공 후 이동 변경] 로그아웃 대신 구매 내역으로 이동 — 원래 import 보존
// import { clearLoginAndGoHome } from '../utils/logout';

const SocketContext = createContext();

// [민팅 결과 팝업] 마켓 목록 다시 조회 이벤트 (MarketCardSection에서 수신)
export const MARKET_LIST_REFRESH_EVENT = 'marketListRefresh';
// [민팅 결과 팝업] 상품 등록 결과 소켓 type (백엔드 nft.service.ts mintsSale)
const MINT_RESULT_TYPES = ['Mint & VC Issue', 'Mint', 'VC Issue'];
// [진행 중 로딩] 결과가 이 시간 안에 오지 않으면 지연 안내
const PROGRESS_DELAY_MS = 2 * 60 * 1000;

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const useSocket = () => {
  return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
  const { showNotification } = useNotification();
  const [socket, setSocket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // [진행 중 로딩] 진행 중인 요청 (한 번에 하나: { kind: 'mint' | 'purchase' | 'reward', key: marketNo 또는 purchaseNo })
  // 소켓 핸들러는 한 번만 등록되므로 최신 값은 ref로 읽음
  const [progress, setProgress] = useState(null);
  const [isProgressDelayed, setIsProgressDelayed] = useState(false);
  const progressRef = useRef(null);
  // [당첨 축하 팝업] rewardStarted로 받은 경품 정보 { purchaseNo, marketNo } — 결과 팝업에서 상품 조회에 사용
  const rewardInfoRef = useRef(null);
  // [글래스 로그인 팝업 2026-10-26] 팝업 내용(glassLogin payload, 닫힘이면 null). 열림 여부는 소켓 핸들러에서 읽으므로 ref도 둠
  const [glassPopup, setGlassPopup] = useState(null);
  const glassOpenRef = useRef(false);

  const closeGlassPopup = useCallback(() => {
    glassOpenRef.current = false;
    setGlassPopup(null);
  }, []);

  const startProgress = useCallback((kind, key) => {
    progressRef.current = { kind, key: Number(key) };
    setProgress(progressRef.current);
    setIsProgressDelayed(false);
  }, []);

  // ref를 즉시 비워 beforeunload 확인창이 바로 해제되도록 함 (페이지 이동 전 호출)
  const endProgress = useCallback(() => {
    progressRef.current = null;
    setProgress(null);
    setIsProgressDelayed(false);
  }, []);

  // [진행 중 로딩] 2분 지나도 결과가 없으면 지연 안내로 전환
  useEffect(() => {
    if (!progress) return undefined;
    const timer = setTimeout(() => setIsProgressDelayed(true), PROGRESS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [progress]);

  // [진행 중 로딩] 로딩 중에만 새로고침·창 닫기 확인창
  useEffect(() => {
    if (!progress) return undefined;
    const handleBeforeUnload = (e) => {
      if (!progressRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [progress]);

  useEffect(() => {
    // [소켓 네임스페이스 수정] apiUrl의 /api가 네임스페이스로 해석되어 연결 거절됨 → origin 사용
    // const newSocket = io(environmentConfig.apiUrl, {
    //   transports: ['websocket'],
    //   secure: true,
    // });
    const socketUrl = new URL(environmentConfig.apiUrl).origin;
    const newSocket = io(socketUrl, {
      transports: ['websocket'],
      secure: true,
    });

    setSocket(newSocket);

    newSocket.on('connect', () => {
      // 재연결 시에도 현재 로그인 지갑으로 등록 (이벤트 로그인으로 지갑이 바뀐 경우 포함)
      const connectedWallet = localStorage.getItem('walletAddress');
      if (connectedWallet) {
        newSocket.emit('registerWallet', connectedWallet);
      }
    });

    // [진행 중 로딩] 경품 지급 시작 — 이벤트 로그인 + 현재 지갑이 구매자일 때 로딩 시작
    newSocket.on('rewardStarted', data => {
      const isEventLogin = localStorage.getItem('eventLogin') === 'Y';
      const currentWallet = (localStorage.getItem('walletAddress') || '').toLowerCase();
      const buyerWallet = (data?.buyerAddress || '').toLowerCase();
      if (isEventLogin && currentWallet && buyerWallet === currentWallet) {
        // [당첨 축하 팝업] 결과 팝업에서 쓸 marketNo 보관
        rewardInfoRef.current = { purchaseNo: Number(data.purchaseNo), marketNo: data.marketNo };
        // [당첨 축하 팝업 미리 받기] 결과 팝업까지 Transfer 처리 시간 동안 미리 받기 (실패 무시)
        prefetchRewardPrize(data.marketNo);
        // [글래스 로그인 팝업 2026-10-26] 글래스 팝업이 열려 있으면 닫고 기존 경품 지급 처리 진행
        glassOpenRef.current = false;
        setGlassPopup(null);
        startProgress('reward', data.purchaseNo);
      }
    });

    // [글래스 로그인 팝업 2026-10-26] 글래스 로그인 — 이벤트 로그인 + 현재 지갑이 그 참가자일 때만 팝업
    // 이미 열려 있거나(앱 재시도로 짧은 간격에 여러 번 옴) 진행 중 로딩(상품 등록·결제·경품 지급) 중이면 띄우지 않음
    newSocket.on('glassLogin', data => {
      const isEventLogin = localStorage.getItem('eventLogin') === 'Y';
      const currentWallet = (localStorage.getItem('walletAddress') || '').toLowerCase();
      const participantWallet = (data?.walletAddress || '').toLowerCase();
      if (!isEventLogin || !currentWallet || participantWallet !== currentWallet) return;
      if (glassOpenRef.current || progressRef.current) return;
      glassOpenRef.current = true;
      setGlassPopup(data);
    });

    newSocket.on('transactionResult', result => {
      setIsLoading(true);
      setTimeout(() => {
        // [이벤트 경품 팝업] 토스트 알림은 사용하지 않음 — 원래 코드 보존
        // if (result.status === 'success') {
        //   if (result.type === 'Mint & VC Issue') {
        //     showNotification('success', '에셋 발행 성공', 'NFT 발행이 성공적으로 완료되었습니다.');
        //   } else {
        //     showNotification('success', '에셋 구매 성공', 'NFT 이전이 성공적으로 완료되었습니다.');
        //   }
        // } else if (result.status === 'failed') {
        //   if (result.type === 'Mint') {
        //     showNotification('error', '에셋 발행 실패', 'NFT 발행이 실패했습니다.');
        //   } else {
        //     showNotification('error', '에셋 구매 실패', 'NFT 이전이 실패했습니다.');
        //   }
        // }

        // [진행 중 로딩] 진행 중인 요청과 같은 결과인지 (종류 + marketNo/purchaseNo)
        const current = progressRef.current;
        const isProgressOf = (kind, key) => !!current && current.kind === kind && current.key === Number(key);

        // [진행 중 로딩] 진행 중인 일반 구매(결제)의 Transfer 결과 → 결제 팝업 (경품 팝업보다 우선)
        const isPurchaseResult = result?.type === 'Transfer' && isProgressOf('purchase', result.purchaseNo);
        if (isPurchaseResult) {
          endProgress();
          if (result.status === 'success') {
            Swal.fire({
              icon: 'success',
              title: '결제가 완료되었습니다',
              confirmButtonText: '확인',
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then(() => {
              // SocketProvider는 라우터 바깥이라 navigate 사용 불가 → 전체 이동
              window.location.href = '/mypage/market/purchase';
            });
          } else if (result.status === 'failed') {
            Swal.fire({
              icon: 'error',
              title: '결제에 실패했습니다',
              html: `<p class="text-sm text-red-600 break-all">${escapeHtml(result.error || '처리 중 오류가 발생했습니다')}</p>`,
              confirmButtonText: '확인',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });
          }
        }

        // [이벤트 경품 팝업] 이벤트 로그인 상태 + Transfer 결과 + 현재 지갑 대상일 때만 팝업
        const isEventLogin = localStorage.getItem('eventLogin') === 'Y';
        const currentWallet = (localStorage.getItem('walletAddress') || '').toLowerCase();
        const buyerWallet = (result?.buyerAddress || '').toLowerCase();

        if (!isPurchaseResult && isEventLogin && result?.type === 'Transfer' && currentWallet && buyerWallet === currentWallet) {
          // [진행 중 로딩] 진행 중인 경품 지급이면 로딩 종료 후 기존 경품 팝업
          if (isProgressOf('reward', result.purchaseNo)) {
            endProgress();
          }

          const detailHtml = `
            <div class="text-left text-sm text-gray-700 break-all">
              <p class="mb-1"><span class="font-semibold">from</span> ${escapeHtml(result.sellerAddress)}</p>
              <p class="mb-1"><span class="font-semibold">to</span> ${escapeHtml(result.buyerAddress)}</p>
              <p><span class="font-semibold">tokenId</span> ${escapeHtml(result.tokenId)}</p>
            </div>
          `;

          if (result.status === 'success') {
            // [당첨 축하 팝업] 기존 경품 성공 팝업 — 원래 코드 보존
            // Swal.fire({
            //   icon: 'success',
            //   title: '경품 지급이 완료되었습니다',
            //   html: detailHtml,
            //   confirmButtonText: '확인',
            //   allowOutsideClick: false,
            //   allowEscapeKey: false,
            // }).then(() => {

            // [당첨 축하 팝업] 같은 purchaseNo의 rewardStarted를 받았으면 marketNo 사용, 놓쳤으면(새로고침 등) purchaseNo로 조회
            const rewardInfo = rewardInfoRef.current;
            const rewardMarketNo = rewardInfo && rewardInfo.purchaseNo === Number(result.purchaseNo) ? rewardInfo.marketNo : null;
            rewardInfoRef.current = null;
            let prizeRoot = null;
            let stopCelebrate = null;

            Swal.fire({
              // 3D 무대가 제목보다 위에 와야 하므로 Swal 제목은 숨기고 본문 컴포넌트에서 그림 (Header 팝업과 같은 구조)
              // 3D 무대가 팝업 위·양옆 끝까지 차도록 popup padding 0 — 본문 여백은 RewardPrizeContent(px-6), 버튼 여백은 actions(px-6 pb-6)
              title: '',
              html: '<div id="reward-prize-root"></div>',
              width: 460,
              padding: 0,
              background: '#ffffff',
              buttonsStyling: false,
              confirmButtonText: '확인',
              allowOutsideClick: false,
              allowEscapeKey: false,
              customClass: {
                // [글래스 로그인 팝업 2026-10-27] 낮은 화면에서 잘림 — 팝업 최대 높이를 화면 높이(컨테이너 여백 0.625rem×2 제외)로, 본문만 팝업 안에서 스크롤하고 확인 버튼은 항상 보임
                // popup: '!rounded-[20px] !overflow-hidden shadow-xl',
                popup:
                  '!rounded-[20px] !overflow-hidden shadow-xl !grid-rows-[minmax(0,1fr)_auto] !max-h-[calc(100vh-1.25rem)] supports-[height:100dvh]:!max-h-[calc(100dvh-1.25rem)]',
                title: 'hidden',
                // htmlContainer: '!m-0 !p-0',
                htmlContainer: '!m-0 !p-0 !min-h-0 !overflow-y-auto',
                actions: '!w-full !box-border !mx-0 !mt-5 !px-6 !pb-6',
                confirmButton:
                  'inline-flex w-full justify-center rounded-xl bg-[#5a45e6] px-4 py-3 text-[15px] font-semibold text-white hover:bg-[#4a37d0] focus:outline-none',
              },
              didOpen: popup => {
                const container = popup.querySelector('#reward-prize-root');
                if (container) {
                  prizeRoot = createRoot(container);
                  prizeRoot.render(
                    <RewardPrizeContent
                      marketNo={rewardMarketNo}
                      purchaseNo={result.purchaseNo}
                      sellerAddress={result.sellerAddress}
                      buyerAddress={result.buyerAddress}
                      tokenId={result.tokenId}
                    />
                  );
                }
                stopCelebrate = celebrate();
              },
              // 확인으로 닫히거나 다른 팝업으로 대체될 때 효과 중지 + 3D 렌더러 정리
              didDestroy: () => {
                if (stopCelebrate) {
                  stopCelebrate();
                  stopCelebrate = null;
                }
                if (prizeRoot) {
                  prizeRoot.unmount();
                  prizeRoot = null;
                }
              },
            }).then(() => {
              // [경품 성공 후 이동 변경] 로그아웃 대신 구매 내역으로 이동 — 원래 코드 보존
              // // 경품 지급 완료 → 로그아웃 (Header 로그아웃과 같은 처리)
              // clearLoginAndGoHome();
              // SocketProvider는 라우터 바깥이라 navigate 사용 불가 → 전체 이동
              window.location.href = '/mypage/market/purchase';
            });
          } else if (result.status === 'failed') {
            Swal.fire({
              icon: 'error',
              title: '경품 지급에 실패했습니다',
              html: `
                <p class="text-sm text-red-600 mb-3 break-all">${escapeHtml(result.error || '실패 사유 없음')}</p>
                ${detailHtml}
              `,
              confirmButtonText: '확인',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });
          }
        }

        // [민팅 결과 팝업] 로그인 상태에서 상품 등록(민팅·VC 발급) 결과 팝업 — 등록자 지갑 방으로만 옴
        const isLoggedIn = !!localStorage.getItem('accessToken');
        if (isLoggedIn && MINT_RESULT_TYPES.includes(result?.type)) {
          // [진행 중 로딩] 진행 중인 상품 등록이면 로딩 종료 후 기존 민팅 결과 팝업
          if (isProgressOf('mint', result.marketNo)) {
            endProgress();
          }

          if (result.status === 'success') {
            Swal.fire({
              icon: 'success',
              title: '상품 등록이 완료되었습니다',
              confirmButtonText: '확인',
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then(() => {
              // 현재 화면이 마켓 목록이면 목록 다시 조회 (SocketProvider는 라우터 바깥 → 이벤트로 전달)
              if (/^\/market\/?$/.test(window.location.pathname)) {
                window.dispatchEvent(new Event(MARKET_LIST_REFRESH_EVENT));
              }
            });
          } else if (result.status === 'failed') {
            Swal.fire({
              icon: 'error',
              title: '상품 등록에 실패했습니다',
              html: `<p class="text-sm text-red-600 break-all">${escapeHtml(result.error || '처리 중 오류가 발생했습니다')}</p>`,
              confirmButtonText: '확인',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });
          }
        }
        setIsLoading(false);
      }, 2000);
    });

    return () => {
      newSocket.disconnect();
    };
  }, [showNotification, startProgress, endProgress]);

  return (
    <SocketContext.Provider value={{ socket, isLoading, startProgress, endProgress }}>
      {children}
      {/* [진행 중 로딩] 화면 이동에도 유지되도록 Provider에서 렌더링 */}
      <ProgressOverlay progress={progress} isDelayed={isProgressDelayed} onClose={endProgress} />
      {/* [글래스 로그인 팝업 2026-10-26] 웹의 어느 화면에서든 표시 */}
      <GlassLoginPopup data={glassPopup} onClose={closeGlassPopup} />
    </SocketContext.Provider>
  );
};


